import { useState, useCallback } from 'react';
import AIApi from '../../api/AIApi';
import fetchApi from '../../util/fetchApi';
import useLoader from '../../util/useLoader';
import useActionReducer from 'src/util/useActionReducer';

/**
 * Opens the chat SSE stream and drives the callbacks. The `listen` endpoint is
 * unauthenticated (opaque single-use sessionId), so a plain EventSource works.
 * Events reuse the generation-stream contract: `progress` (status sub-line),
 * `token` (cumulative answer text so far), `final` (full chat DTO). Resolves on
 * `final`, rejects on stream error so the caller can fall back to non-streaming.
 */
async function consumeChatStream(sessionId, { onStatus, onToken, onFinal }) {
  // fetchApi.api.url(...) is async (returns a Promise) — must await it, otherwise
  // `new EventSource(promise)` stringifies to "[object Promise]" and 404s.
  const url = await fetchApi.api.url(`ai/chatStream/listen/${sessionId}`);
  return new Promise((resolve, reject) => {
    let settled = false;
    const es = new EventSource(url);

    const done = (fn, arg) => {
      if (settled) return;
      settled = true;
      es.close();
      fn(arg);
    };

    es.onmessage = e => {
      let ev;
      try { ev = JSON.parse(e.data); } catch { return; }
      if (ev.type === 'token') onToken?.(ev.message ?? '');
      // Prefer the language-neutral progressKind (frontend localizes it); fall
      // back to the server message only if no kind was sent.
      else if (ev.type === 'progress') onStatus?.(ev.progressKind || ev.message || '');
      else if (ev.type === 'final') { onFinal?.(ev); done(resolve); }
      else if (ev.type === 'error') { done(reject, new Error(ev.errors?.[0] || 'stream error')); }
    };
    es.onerror = () => done(reject, new Error('stream connection error'));
  });
}

function useAIChat(props) {
  const { agent } = props;
  const [state, dispatch] = useActionReducer(chatActions);

  const { messages, conversationId, chatContext, isOpen } = state;

  const [agentTyping, agentError, agentLoadFn] = useLoader();
  // Live status shown in the typing indicator while the stream is in flight
  // ("Analizando…", "Consultando datos…", "Redactando respuesta…").
  const [agentStatus, setAgentStatus] = useState(null);

  const clearChat = useCallback(() => dispatch.clearChat(), [dispatch]);
  const closeChat = useCallback(() => dispatch.close(), [dispatch]);
  const toggleOpenChat = useCallback(() => dispatch.toggleOpen(), [dispatch]);

  const finalizeFromResponse = useCallback(
    response => {
      setAgentStatus(null);
      const aiResponse = response?.messages?.slice(-1)[0];
      dispatch.finishStreamingMessage({
        message: aiResponse?.content ?? 'No response was received.',
        conversationId: response?.conversationId,
        chatContext: { identifierCases: response?.identifierCases },
      });
    },
    [dispatch]
  );

  const sendMessage = useCallback(
    message => {
      const history = messages.filter(x => x.sender !== 'System');
      dispatch.addSendMessage({ message });

      agentLoadFn(async () => {
        // Placeholder assistant bubble that fills in live as tokens stream.
        dispatch.addStreamingMessage({ sender: agent.name });
        setAgentStatus('…');

        try {
          const { sessionId } = await AIApi.chatStreamStart({
            content: message,
            messages: history,
            conversationId,
          });

          await consumeChatStream(sessionId, {
            onStatus: s => setAgentStatus(s),
            // Once real text arrives, drop the status label so the indicator
            // reflects the answer being typed rather than a stale phase.
            onToken: text => { setAgentStatus(null); dispatch.updateStreamingMessage({ message: text }); },
            onFinal: ev => finalizeFromResponse(ev.chat),
          });
        } catch (streamError) {
          // Fallback to the non-streaming endpoint so a stream/CORS/proxy hiccup
          // never breaks the chat — the user still gets the full answer.
          try {
            const response = await AIApi.chat({
              content: message,
              messages: history,
              conversationId,
            });
            finalizeFromResponse(response);
          } catch (e) {
            dispatch.finishStreamingMessage({
              message: `An error ocurred while trying to chat with ${agent.name}.`,
              error: true,
            });
          }
        } finally {
          setAgentStatus(null);
        }
      });
    },
    [agentLoadFn, dispatch, messages, conversationId, agent.name, finalizeFromResponse]
  );

  return {
    agent,
    agentTyping,
    agentError,
    agentStatus,
    messages,
    sendMessage,
    isOpen,
    toggleOpenChat,
    closeChat,
    chatContext,
    clearChat,
  };
}

const chatActions = {
  initialize: () => ({
    isOpen: false,
    messages: [],
    conversationId: '',
    chatContext: undefined,
  }),

  toggleOpen: (_, { isOpen }) => ({ isOpen: !isOpen }),
  close: () => ({ isOpen: false }),

  clearChat: () => ({
    messages: [],
    chatContext: undefined,
    conversationId: '',
  }),

  addSendMessage: ({ message }, state) =>
    chatActions.addMessage(
      {
        message: {
          message,
          sender: 'You',
          direction: 'outgoing',
        },
      },
      state
    ),

  addRecvMessage: ({ message, sender, conversationId, chatContext }, state) =>
    chatActions.addMessage(
      {
        message: {
          message,
          sender,
          direction: 'incoming',
        },
        conversationId,
        chatContext,
      },
      state
    ),

  addSystemMessage: ({ error, message }, state) =>
    chatActions.addMessage(
      {
        message: {
          message,
          sender: 'System',
          direction: 'incoming',
          extra: error ? { error: true } : {},
        },
      },
      state
    ),

  // --- Streaming assistant bubble (SSE) ---
  addStreamingMessage: ({ sender }, state) =>
    chatActions.addMessage(
      {
        message: {
          message: '',
          sender,
          direction: 'incoming',
          extra: { streaming: true },
        },
      },
      state
    ),

  updateStreamingMessage: ({ message }, state) => {
    const msgs = [...(state?.messages ?? [])];
    if (msgs.length === 0) return {};
    const last = msgs[msgs.length - 1];
    msgs[msgs.length - 1] = { ...last, message, streaming: true };
    return { messages: msgs };
  },

  finishStreamingMessage: ({ message, error, conversationId, chatContext }, state) => {
    const msgs = [...(state?.messages ?? [])];
    if (msgs.length === 0) return {};
    const last = msgs[msgs.length - 1];
    msgs[msgs.length - 1] = {
      ...last,
      message: message ?? last.message,
      streaming: false,
      ...(error ? { error: true } : {}),
    };
    return {
      messages: msgs,
      conversationId: conversationId ?? state?.conversationId,
      chatContext: chatContext ?? state?.chatContext,
    };
  },

  addMessage: ({ message: { message, sentTime, sender, direction, extra }, conversationId, chatContext }, state) => {
    const prevMessage = state?.messages ? state.messages[state.messages.length - 1] : undefined;
    const newMessage = {
      message: message ?? '',
      sentTime: sentTime ?? 'just now', // [gio]: Maybe this should have proper timestamps????
      sender: sender ?? 'unknown',
      isFirst: !prevMessage,
      isContinuation: false,
      direction: direction ?? 'incoming',
      ...(extra ?? {}),
    };

    newMessage.isContinuation = prevMessage?.sender == newMessage.sender && prevMessage?.direction == newMessage.direction;

    return {
      conversationId: conversationId ?? state?.conversationId,
      chatContext: chatContext ?? state?.chatContext,
      messages: [...(state?.messages ?? []), newMessage],
    };
  },
};

export default useAIChat;
